import { useCallback, useMemo } from 'react';

import type { PaymentMethodProvider } from '@sorare/core/src/__generated__/globalTypes';
import {
  Sum,
  useMonetaryAmount,
} from '@sorare/core/src/hooks/useMonetaryAmount';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';
import { isStripeAccount } from '@sorare/core/src/lib/paymentMethod';

type Props = {
  isFiat: boolean;
  monetaryAmount: MonetaryAmount;
  creditCardFee: number;
  activeFee: boolean;
  isCreditCard?: boolean;
  potentialDiscount?: MonetaryAmount;
  cardPaymentProvider?: PaymentMethodProvider;
};
const STRIPE_MINIMUM_AMOUNT = 50;

export const useCalculateAmounts = ({
  isFiat,
  monetaryAmount,
  creditCardFee,
  activeFee,
  isCreditCard,
  potentialDiscount = MonetaryAmount.zero,
  cardPaymentProvider,
}: Props) => {
  const { usd } = useMonetaryAmount();

  const absoluteConversionCreditMonetaryAmount = potentialDiscount;
  const conversionCreditMonetaryAmount = useMemo(
    () => absoluteConversionCreditMonetaryAmount.multipiedBy(-1),
    [absoluteConversionCreditMonetaryAmount]
  );

  const feesBasisPoints = useMemo(
    () => (activeFee ? creditCardFee : 0),
    [activeFee, creditCardFee]
  );

  // The backend deducts the conversion credit *before* applying the fee rate
  // (TradeAmounts#send_amount then #added_fee_amount), so the fee base is the
  // discounted amount, not the sticker price.
  const discountedMonetaryAmount = useMemo(
    () => Sum([monetaryAmount, conversionCreditMonetaryAmount]),
    [monetaryAmount, conversionCreditMonetaryAmount]
  );

  const feesMonetaryAmount: MonetaryAmount = useMemo(() => {
    const fees =
      discountedMonetaryAmount.calculateFeesFromBasisPoints(feesBasisPoints);
    // MonetaryAmounts.round on the backend rounds half-up to the minor unit,
    // which Math.round matches for the positive amounts a fee can take. Only
    // the fiat legs need it: calculateFeesFromBasisPoints divides wei and
    // lamport as bigints, so those are already whole (truncated) minor units.
    return new MonetaryAmount({
      // calculateFeesFromBasisPoints keeps its receiver's reference currency,
      // and Sum's is always zero's -- keep the price's, as before.
      referenceCurrency: monetaryAmount.referenceCurrency,
      eurCents: Math.round(fees.eurCents),
      usdCents: Math.round(fees.usdCents),
      gbpCents: Math.round(fees.gbpCents),
      wei: fees.wei,
      lamport: fees.lamport,
    });
  }, [
    discountedMonetaryAmount,
    feesBasisPoints,
    monetaryAmount.referenceCurrency,
  ]);

  const totalMonetaryAmount = useMemo(() => {
    return Sum(
      isFiat
        ? [discountedMonetaryAmount, feesMonetaryAmount]
        : [discountedMonetaryAmount]
    );
  }, [isFiat, discountedMonetaryAmount, feesMonetaryAmount]);

  const payWithConversionCreditsOnly =
    totalMonetaryAmount.isZero && conversionCreditMonetaryAmount.wei < 0n;

  // For stripe, we cannot charge less than 0.5$. The 2 following hooks are used to display this minimum if needed
  const isBellowStripeMinimum =
    !payWithConversionCreditsOnly &&
    isCreditCard &&
    isStripeAccount(cardPaymentProvider) &&
    totalMonetaryAmount.usdCents < STRIPE_MINIMUM_AMOUNT;

  const actualTotalMonetaryAmount = useMemo(() => {
    if (isBellowStripeMinimum) {
      return usd(STRIPE_MINIMUM_AMOUNT);
    }
    return totalMonetaryAmount;
  }, [totalMonetaryAmount, isBellowStripeMinimum, usd]);

  const actualConversionCreditMonetaryAmount = useCallback(
    (ma: MonetaryAmount) => {
      if (isBellowStripeMinimum) {
        return usd(STRIPE_MINIMUM_AMOUNT - monetaryAmount.usdCents);
      }
      return ma;
    },
    [monetaryAmount.usdCents, isBellowStripeMinimum, usd]
  );

  return {
    fees: feesBasisPoints / 10000,
    feesMonetaryAmount,
    conversionCreditMonetaryAmount: actualConversionCreditMonetaryAmount(
      conversionCreditMonetaryAmount
    ),
    absoluteConversionCreditMonetaryAmount:
      actualConversionCreditMonetaryAmount(
        absoluteConversionCreditMonetaryAmount
      ),
    maxDiscountMonetary: potentialDiscount,
    totalMonetaryAmount: actualTotalMonetaryAmount,
    initialAmountIsBellowStripeMinimum: isBellowStripeMinimum,
    payWithConversionCreditsOnly,
  };
};
